import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validateCandidateEmailFormat } from '@/utils/emailValidation';
import { validateAndFormatPhone } from '@/utils/phoneValidation'; // Réactivé pour éviter crash (champs restent cachés)
// import { Check, X } from 'lucide-react'; // DÉSACTIVÉ - SMS désactivé

interface CandidateData {
    candidate_name: string;
    candidate_email: string;
    candidate_phone?: string;
    target_position: string;
}

interface EditCandidateModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentData: CandidateData;
    onSave: (data: CandidateData) => void;
}

interface FormErrors {
    candidate_name?: string;
    candidate_email?: string;
    candidate_phone?: string;
    target_position?: string;
}

export const EditCandidateModal = ({
    isOpen,
    onClose,
    currentData,
    onSave,
}: EditCandidateModalProps) => {
    const [formData, setFormData] = useState<CandidateData>(currentData);
    const [errors, setErrors] = useState<FormErrors>({});
    const [_phoneValid, setPhoneValid] = useState<boolean>(false); // Conservé pour compatibilité (champ caché)
    const [touched, setTouched] = useState<Record<string, boolean>>({
        candidate_name: false,
        candidate_email: false,
        candidate_phone: false,
        target_position: false,
    });

    // Réinitialiser le formulaire quand la modal s'ouvre
    useEffect(() => {
        if (isOpen) {
            setFormData(currentData);
            setErrors({});
            setPhoneValid(currentData.candidate_phone ? validateAndFormatPhone(currentData.candidate_phone).isValid : false);
            setTouched({
                candidate_name: false,
                candidate_email: false,
                candidate_phone: false,
                target_position: false,
            });
        }
    }, [isOpen, currentData]);

    const validateField = (field: string, value: string): string | undefined => {
        switch (field) {
            case 'candidate_name':
                if (!value.trim()) {
                    return 'Le nom est requis';
                }
                break;
            case 'candidate_email':
                if (!value.trim()) {
                    return 'L\'email est requis';
                }
                if (!validateCandidateEmailFormat(value)) {
                    return 'Format d\'email invalide';
                }
                break;
            case 'candidate_phone':
                // Téléphone optionnel, mais si rempli doit être valide
                if (value.trim()) {
                    const phoneResult = validateAndFormatPhone(value);
                    if (!phoneResult.isValid) {
                        return phoneResult.error || 'Format invalide';
                    }
                }
                break;
            case 'target_position':
                if (!value.trim()) {
                    return 'Le poste est requis';
                }
                break;
        }
        return undefined;
    };

    const handleChange = (field: string, value: string) => {
        // Gestion spéciale pour le téléphone
        if (field === 'candidate_phone') {
            const phoneResult = validateAndFormatPhone(value);
            setFormData(prev => ({
                ...prev,
                [field]: phoneResult.formatted || value
            }));
            setPhoneValid(phoneResult.isValid);
            
            if (touched[field]) {
                setErrors(prev => ({
                    ...prev,
                    [field]: phoneResult.isValid ? undefined : (phoneResult.error || 'Format invalide')
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));

            // Validation en temps réel si le champ a déjà été touché
            if (touched[field]) {
                const error = validateField(field, value);
                setErrors(prev => ({
                    ...prev,
                    [field]: error
                }));
            }
        }
    };

    const handleBlur = (field: string) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        const value = formData[field as keyof CandidateData] || '';
        const error = validateField(field, value);
        setErrors(prev => ({
            ...prev,
            [field]: error
        }));
    };

    const handleSave = () => {
        // Valider tous les champs
        const newErrors: FormErrors = {};
        let hasErrors = false;

        const fieldsToValidate = ['candidate_name', 'candidate_email', 'candidate_phone', 'target_position'];
        fieldsToValidate.forEach(field => {
            const value = formData[field as keyof CandidateData] || '';
            const error = validateField(field, value);
            if (error) {
                newErrors[field as keyof FormErrors] = error;
                hasErrors = true;
            }
        });

        if (hasErrors) {
            setErrors(newErrors);
            setTouched({
                candidate_name: true,
                candidate_email: true,
                candidate_phone: true,
                target_position: true,
            });
            return;
        }

        
        onSave(formData);
        onClose();
    };

    const handleCancel = () => {
        setFormData(currentData);
        setErrors({});
        setPhoneValid(false);
        setTouched({
            candidate_name: false,
            candidate_email: false,
            candidate_phone: false,
            target_position: false,
        });
        onClose();
    };

    // VALIDATION TÉLÉPHONE DÉSACTIVÉE - Le champ téléphone n'est plus requis
    const isFormValid = !Object.values(errors).some(error => error !== undefined) &&
                        formData.candidate_name.trim() &&
                        formData.candidate_email.trim() &&
                        validateCandidateEmailFormat(formData.candidate_email) &&
                        formData.target_position.trim();

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] p-0 gap-0">
                <DialogHeader className="p-6 pb-4">
                    <DialogTitle className="text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
                        Informations du candidat
                    </DialogTitle>
                </DialogHeader>

                <div className="px-6 pb-6 space-y-4">
                    <div className="space-y-2">
                        <Label className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                            Nom complet
                        </Label>
                        <Input
                            value={formData.candidate_name}
                            onChange={(e) => handleChange('candidate_name', e.target.value)}
                            onBlur={() => handleBlur('candidate_name')}
                            placeholder="Nom du candidat"
                            className={`w-full px-3 py-3.5 bg-white rounded-[10px] border text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none ${
                                errors.candidate_name ? 'border-red-500' : 'border-neutral-400/20'
                            }`}
                        />
                        {errors.candidate_name && (
                            <p className="text-red-500 text-xs font-['Bricolage_Grotesque']">
                                {errors.candidate_name}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                            Email
                        </Label>
                        <Input
                            type="email"
                            value={formData.candidate_email}
                            onChange={(e) => handleChange('candidate_email', e.target.value)}
                            onBlur={() => handleBlur('candidate_email')}
                            placeholder="email@exemple.com"
                            className={`w-full px-3 py-3.5 bg-white rounded-[10px] border text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none ${
                                errors.candidate_email ? 'border-red-500' : 'border-neutral-400/20'
                            }`}
                        />
                        {errors.candidate_email && (
                            <p className="text-red-500 text-xs font-['Bricolage_Grotesque']">
                                {errors.candidate_email}
                            </p>
                        )}
                    </div>

                    {/* CHAMP TÉLÉPHONE DÉSACTIVÉ - Pour réactiver, décommenter le bloc ci-dessous 
                    <div className="space-y-2">
                        <Label className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                            Numéro de téléphone *
                        </Label>
                        <div className="relative">
                            <Input
                                type="tel"
                                value={formData.candidate_phone || ''}
                                onChange={(e) => handleChange('candidate_phone', e.target.value)}
                                onBlur={() => handleBlur('candidate_phone')}
                                placeholder="+33612345678"
                                className={`w-full px-3 py-3.5 pr-10 bg-white rounded-[10px] border text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:ring-1 focus:outline-none ${
                                    errors.candidate_phone 
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                                        : phoneValid
                                        ? 'border-teal-400 focus:border-teal-400 focus:ring-teal-400'
                                        : 'border-neutral-400/20 focus:border-teal-400 focus:ring-teal-400'
                                }`}
                            />
                            {formData.candidate_phone && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    {phoneValid ? (
                                        <Check className="h-5 w-5 text-teal-400" />
                                    ) : (
                                        <X className="h-5 w-5 text-red-400" />
                                    )}
                                </div>
                            )}
                        </div>
                        {errors.candidate_phone && (
                            <p className="text-red-500 text-xs font-['Bricolage_Grotesque']">
                                {errors.candidate_phone}
                            </p>
                        )}
                        {!errors.candidate_phone && formData.candidate_phone && (
                            <p className="text-neutral-400 text-xs font-['Bricolage_Grotesque']">
                                Format: +33612345678
                            </p>
                        )}
                    </div>
                    */}

                    <div className="space-y-2">
                        <Label className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                            Poste visé
                        </Label>
                        <Input
                            value={formData.target_position}
                            onChange={(e) => handleChange('target_position', e.target.value)}
                            onBlur={() => handleBlur('target_position')}
                            placeholder="Titre du poste"
                            className={`w-full px-3 py-3.5 bg-white rounded-[10px] border text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none ${
                                errors.target_position ? 'border-red-500' : 'border-neutral-400/20'
                            }`}
                        />
                        {errors.target_position && (
                            <p className="text-red-500 text-xs font-['Bricolage_Grotesque']">
                                {errors.target_position}
                            </p>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            onClick={handleCancel}
                            variant="outline"
                            className="px-4 py-2 rounded-[10px] border border-neutral-400/20 text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] hover:bg-gray-50"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!isFormValid}
                            className="px-4 py-2 bg-teal-400 hover:bg-teal-500 rounded-[10px] text-white text-sm font-medium font-['Bricolage_Grotesque'] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Sauvegarder
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};